import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { Audience, TopicProfile } from '@/cms/topics';
import type { JourneyRow } from '@/lib/topic-state';
import { clearLanguageCookie, ENGLISH_CODE, writeLanguageCookie } from '@/i18n/languages';
import { journeyRowsFor } from '@/lib/topic-state';
import { useChatProgressStore } from './chat-progress';

/** The four beach buddies. Their artwork lives in `public/images/character-*.webp`. */
export const BUDDIES = ['kangaroo', 'koala', 'duck', 'squirrel'] as const;

export type Buddy = (typeof BUDDIES)[number];

export const DEFAULT_BUDDY: Buddy = 'kangaroo';

/**
 * The language a profile actually gets. The kids course and its UI are English
 * only — none of it is translated — so a child is always `en`, whatever the
 * welcome screen's picker said. Every write to a profile goes through this.
 */
export function courseLanguage(audience: Audience, language: string): string {
  return audience === 'child' ? ENGLISH_CODE : language;
}

/** Everything onboarding collects, plus the two flags the passport flow sets. */
export type Profile = {
  /** Lower-case language code — a `language` document's UID. */
  language: string;
  audience: Audience;
  /** Absent until the learner completes the name screen. */
  name?: string;
  buddy: Buddy;
  /** True once every module valid for this profile has a score. */
  completed: boolean;
  certificateId?: string;
  isAustralian?: boolean;
};

/**
 * Per-module counters, keyed by the topic's `module_key` so progress survives a
 * language switch. Semantics are the live app's, verbatim: every enabled slice
 * counts toward `nSlices`, `nTried` rises on each answer attempt and
 * `nAnswered` only on a correct one, and `bestScore` — set by `finishModule` —
 * is the de-facto "module done" flag.
 */
export type ModuleProgress = {
  nSlices: number;
  nCompleted: number;
  nAnswered: number;
  nTried: number;
  bestScore?: number;
};

export type UserState = {
  profile: Profile | null;
  modules: Record<string, ModuleProgress>;
  /** The passport celebration plays once per device. */
  passportCelebrated: boolean;
  /** Set once the legacy IndexedDB import has been attempted. */
  migrated: boolean;
};

/** What `setProfile` accepts. Anything omitted keeps its current value. */
export type ProfilePatch = Partial<Profile>;

export type UserActions = {
  /**
   * Switches language. Writes the cookie the root loader reads as well as the
   * store, so `<html lang dir>` and every string follow on the next
   * navigation or revalidation. Safe before a profile exists (the `/start`
   * screen calls it on the first of its two taps).
   */
  setLanguage: (code: string) => void;
  /** Creates the profile with sensible defaults, or patches the existing one. */
  setProfile: (patch: ProfilePatch) => void;
  /** Entering a module resets its counters and keeps its best score. */
  startModule: (moduleKey: string, nSlices: number) => void;
  completeSlice: (moduleKey: string) => void;
  recordAttempt: (moduleKey: string, correct: boolean) => void;
  /** Records the module's star rating, never lowering a previous one. */
  finishModule: (moduleKey: string, score: number) => void;
  celebratePassport: () => void;
  /** Start over: this store, the language cookie, and every saved chat run. */
  resetAll: () => void;
};

export type UserStore = UserState & UserActions;

const emptyState: UserState = {
  profile: null,
  modules: {},
  passportCelebrated: false,
  migrated: false,
};

const emptyProgress: ModuleProgress = { nSlices: 0, nCompleted: 0, nAnswered: 0, nTried: 0 };

const defaultProfile: Profile = {
  language: ENGLISH_CODE,
  audience: 'adult',
  buddy: DEFAULT_BUDDY,
  completed: false,
};

/**
 * `localStorage` on the client, and a null storage on the server so a module
 * that reaches the server bundle neither throws nor warns. Nothing renders from
 * this store during SSR: every screen that reads it guards with a
 * `clientLoader` and an invisible `HydrateFallback`.
 */
const storage = createJSONStorage<UserState>(() =>
  typeof window === 'undefined' ? { getItem: () => null, setItem: () => {}, removeItem: () => {} } : localStorage,
);

/**
 * The learner's profile and progress, persisted on the device. There is no
 * account and no server copy — this store is the whole user model.
 */
export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      ...emptyState,

      setLanguage: (code) => {
        const { profile } = get();
        const language = profile ? courseLanguage(profile.audience, code) : code;

        writeLanguageCookie(language);

        if (profile) {
          set({ profile: { ...profile, language } });
        }
      },

      setProfile: (patch) => {
        const current = get().profile ?? defaultProfile;
        const next = { ...current, ...patch };

        next.language = courseLanguage(next.audience, next.language);

        // A certificate belongs to one course type. Changing type drops it, so
        // finishing the new course composes a fresh one.
        if (patch.audience !== undefined && patch.audience !== current.audience) {
          delete next.certificateId;
          next.completed = false;
        }

        // Idempotent, and cheaper than working out whether the language moved.
        writeLanguageCookie(next.language);

        set({ profile: next });
      },

      startModule: (moduleKey, nSlices) =>
        set((state) => ({
          modules: {
            ...state.modules,
            [moduleKey]: {
              ...emptyProgress,
              nSlices,
              bestScore: state.modules[moduleKey]?.bestScore,
            },
          },
        })),

      completeSlice: (moduleKey) =>
        set((state) => {
          const progress = state.modules[moduleKey];

          if (!progress) {
            return state;
          }

          return { modules: { ...state.modules, [moduleKey]: { ...progress, nCompleted: progress.nCompleted + 1 } } };
        }),

      recordAttempt: (moduleKey, correct) =>
        set((state) => {
          const progress = state.modules[moduleKey];

          if (!progress) {
            return state;
          }

          return {
            modules: {
              ...state.modules,
              [moduleKey]: {
                ...progress,
                nTried: progress.nTried + 1,
                nAnswered: progress.nAnswered + (correct ? 1 : 0),
              },
            },
          };
        }),

      finishModule: (moduleKey, score) =>
        set((state) => {
          const progress = state.modules[moduleKey] ?? emptyProgress;

          return {
            modules: {
              ...state.modules,
              [moduleKey]: { ...progress, bestScore: Math.max(progress.bestScore ?? 0, score) },
            },
          };
        }),

      celebratePassport: () => set({ passportCelebrated: true }),

      // The legacy import is deliberately not undone: a learner who starts over
      // wants a clean slate, not their old Dexie progress back. The language
      // cookie *is* cleared: it is the one piece of state the server reads on
      // its own, so leaving it behind brought back an Arabic, right-to-left
      // welcome screen for a learner with no profile at all.
      resetAll: () => {
        clearLanguageCookie();
        // The one cross-store call in the app. Start over is one tap, and a
        // half-finished module left behind in the chat store would resume
        // itself the next time that module was opened.
        useChatProgressStore.getState().clearAll();
        set({ ...emptyState, migrated: get().migrated });
      },
    }),
    {
      name: 'beach-passport',
      version: 2,
      storage,
      // v2: kids became English-only, so a saved child profile in another
      // language is moved to `en`, cookie included, so the next render agrees.
      migrate: (persisted, version) => {
        const state = persisted as UserState;

        if (version < 2 && state.profile) {
          state.profile.language = courseLanguage(state.profile.audience, state.profile.language);
          writeLanguageCookie(state.profile.language);
        }

        return state;
      },
      partialize: ({ profile, modules, passportCelebrated, migrated }) => ({
        profile,
        modules,
        passportCelebrated,
        migrated,
      }),
    },
  ),
);

// --- Selectors ---------------------------------------------------------------

/** A module is complete once it has a score. `bestScore` is the done flag. */
export function isModuleComplete(state: UserState, moduleKey: string): boolean {
  return state.modules[moduleKey]?.bestScore !== undefined;
}

/**
 * The course is complete when every module valid for this profile's audience
 * and language has a score. Pass the `module_key`s of the topics
 * `validTopicsFor` returned — an empty list is never complete, because it means
 * the topics have not loaded.
 */
export function isCourseComplete(state: UserState, validModuleKeys: Array<string>): boolean {
  return validModuleKeys.length > 0 && validModuleKeys.every((key) => isModuleComplete(state, key));
}

/**
 * The learner's own modules, in journey order, and whether they have finished
 * every one of them.
 *
 * Each reward screen (`/passport`, `/get-passport`, the certificate page) needs
 * both facts and neither is knowable on the server, so all three call this from
 * their `clientLoader` and hand the result to `guardCourseComplete`. It also
 * yields the honest `total` for `OverallProgress` — three modules for a
 * teenager, four for everyone else.
 */
export function courseFor<T extends JourneyRow>(
  state: UserState,
  rows: Array<T>,
): { rows: Array<T>; done: number; total: number; complete: boolean } {
  const profile = topicProfile(state);
  const mine = profile ? journeyRowsFor(profile, rows) : [];
  const done = mine.filter((row) => isModuleComplete(state, row.moduleKey)).length;

  return {
    rows: mine,
    done,
    total: mine.length,
    complete: isCourseComplete(
      state,
      mine.map((row) => row.moduleKey),
    ),
  };
}

/** The audience + language pair `validTopicsFor` filters on. */
export function topicProfile(state: UserState): TopicProfile | null {
  return state.profile ? { audience: state.profile.audience, language: state.profile.language } : null;
}

/** Onboarding is finished once the learner has a profile with a name. */
export function hasCompleteProfile(profile: Profile | null): boolean {
  return profile?.name !== undefined && profile.name !== '';
}

/** The theme segment a profile's screens live under: kids get `/h/beach`. */
export function themeForAudience(audience: Audience): 'beach' | 'ocean' {
  return audience === 'child' ? 'beach' : 'ocean';
}

// --- Hooks ------------------------------------------------------------------

export const useProfile = () => useUserStore((state) => state.profile);

export const useModules = () => useUserStore((state) => state.modules);

export const useIsCourseComplete = (validModuleKeys: Array<string>) =>
  useUserStore((state) => isCourseComplete(state, validModuleKeys));

/**
 * Action identities never change, so selecting one is render-stable:
 *
 *   const setProfile = useUserStore((state) => state.setProfile);
 *
 * Outside React — a `clientLoader`, an effect cleanup — reach for
 * `useUserStore.getState()` instead.
 */
